import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { captureLanding, saveClientMode } from "../lib/retailAttribution";
import { Loader2 } from "lucide-react";
import { cleanImageUrl } from "../lib/retailFormat";
import useRetailCatalog from "../hooks/retail/useRetailCatalog";
import useProductSearch, {
  useFacetBrowse, useSelectedProduct, createProductSelectionHandler,
} from "../hooks/retail/useProductSearch";
import { filterCategories } from "../lib/productSearch";
import {
  FAMILY_ALL, buildFamilyTabs, familiesFromCategories,
  filterCategoriesByFamily, resolveActiveFamily,
} from "../lib/productFamily";
import useRetailCart from "../hooks/retail/useRetailCart";
import useCatalogFilters from "../hooks/retail/useCatalogFilters";
import useRetailOrderModes from "../hooks/retail/useRetailOrderModes";
import useRetailFavorites from "../hooks/retail/useRetailFavorites";
import SalesOrderLanding from "../components/retail/customer/SalesOrderLanding";
import WelcomeModal from "../components/retail/customer/WelcomeModal";
import KioskShell from "../components/retail/customer/KioskShell";
import MobileCartDrawer from "../components/retail/customer/MobileCartDrawer";
import CheckoutModal from "../components/retail/customer/CheckoutModal";
import ChatEntryPanel from "../components/retail/customer/ChatEntryPanel";
import ProductInfoModal from "../components/retail/customer/ProductInfoModal";
import api from "../lib/api";
import ConfirmNewOrderModal from "../components/retail/customer/ConfirmNewOrderModal";
import OrderConfirmedModal from "../components/retail/customer/OrderConfirmedModal";
import AwaitingPaymentModal from "../components/retail/customer/AwaitingPaymentModal";
import PrintReceipt from "../components/retail/customer/PrintReceipt";
import {
  customerKey, saveLinkSlug, loadOrderAccessToken,
  saveVerifiedProof, loadVerifiedProof, clearVerifiedProof,
} from "../lib/retailSessionKeys";
import { indexCartItems } from "../lib/retail/cartLineKey";
import { buildAssistantContext, saveAssistantContext } from "../lib/aiAssistantContext";
import useHostReferral from "../hooks/retail/useHostReferral";
import HostReferralBanner from "../components/hospitality/HostReferralBanner";

// Phase 3K/3L/3M — Restaurant-kiosk styled storefront. Layout/visuals match
// the reference Claude kiosk design (dark-green theme + orange CTAs,
// persistent right-side cart on desktop/tablet, sticky cart drawer on
// mobile). API surface is unchanged — still talks to /retail/public/{slug}/*.
// After Phase 3M this file is a thin orchestrator; presentational pieces
// live under components/retail/customer/ and cart state in hooks/retail/.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;
const SESSION_KEY = "retail_session_id";
// Security-audit correction (Aug 2026, round 3) — how long a verified
// member's Favorites/membership display survives with NO page
// interaction before this tab treats the session as abandoned.
// Deliberately SHORTER than the backend's own absolute proof lifetime
// (`VERIFIED_TOKEN_FRESHNESS_SECONDS`, 15 minutes) — these are two
// distinct deadlines, not one: this one catches a genuinely abandoned
// kiosk quickly; the separate absolute-expiry effect below is the
// outer bound for a session someone is ACTIVELY still using, which
// activity can never push past. Earlier revisions of this correction
// set both to 15 minutes, which made the idle timer nearly redundant
// (the two would almost always fire at the same instant); this value
// is intentionally its own, shorter policy.
const MEMBER_SESSION_IDLE_MS = 5 * 60 * 1000;

// ----- helpers --------------------------------------------------------------
// PR #41 hardening — `customerKey`/`saveLinkSlug` now live in
// `lib/retailSessionKeys.js`, the single source of truth for these
// formats, since `RetailPaymentReturn.jsx` and `RetailOrderStatus.jsx`
// need the exact same key shape when they clear/restore session state on
// the way back from checkout.

function loadCustomer(slug) {
  try {
    const raw = sessionStorage.getItem(customerKey(slug));
    return raw ? JSON.parse(raw) : null;
  } catch (_e) { return null; }
}

function saveCustomer(slug, c) {
  // Restaurant Pilot Readiness (Mar 2026) — stamp `last_seen_at` so the
  // resume-order banner can window the 24h window correctly.
  const stamped = { ...(c || {}), last_seen_at: new Date().toISOString() };
  try { sessionStorage.setItem(customerKey(slug), JSON.stringify(stamped)); } catch (_e) { /* noop */ }
}

// ----- main component -------------------------------------------------------

export default function RetailCustomerPage() {
  const { slug, linkSlug } = useParams();

  // PR #41 hardening — Phase 4. Remember the catalog-specific entry
  // linkSlug for this tenant this session, so a later return from
  // checkout (Stripe redirect, order status page) can rebuild THIS
  // storefront's URL instead of falling through to the bare
  // `/retail/:slug` route, which re-resolves to "any published
  // catalog" for the tenant and can silently swap a Parcel Shipping-
  // only catalog for an unrelated one.
  useEffect(() => {
    if (linkSlug) saveLinkSlug(slug, linkSlug);
  }, [slug, linkSlug]);

  const { loading, gateError, catalogData } = useRetailCatalog(slug, linkSlug);

  // Iter43 P0 — thread the link-resolved catalog id into cart ops so the
  // backend binds the cart to the SAME catalog the storefront renders.
  // Without this, non-default storefronts (e.g. /retail/{tenant}/{link_slug})
  // get `product_not_found` on every Add because the cart endpoint
  // falls back to the resolver winner.
  const activeCatalogId = catalogData?.catalog?.id || null;

  const {
    sessionId, cart, setCart, addProduct, setQty, removeItem, setItemTakeout,
    // Gate C5A-3A — items from a cart `checkout()` already spent, whose
    // draft was never paid. See the banner below.
    recoverableCart, recoverCart, recovering,
  } =
    useRetailCart({ slug, gateError, catalogId: activeCatalogId });

  const navigate = useNavigate();
  const location = useLocation();
  // Marketing P0-4 — record a campaign landing (utm_* / mb_cid) for THIS
  // storefront slug. Path only, never the query string; a direct visit
  // changes nothing; unavailable storage or odd parameters are ignored.
  // Reporting hint only — nothing on the page depends on it.
  useEffect(() => {
    captureLanding({ search: location.search, pathname: location.pathname, slug, linkSlug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, linkSlug, location.search]);

  // Phase 3L.1 Pass B — full-page Chat route lives at /retail/{slug}/chat.
  // We pass the customer's current path as `return=` so the assistant can
  // bring them back exactly where they came from when they tap Start Sales
  // Order (kiosk default route OR a specific link-profile route).
  const goToChat = () => {
    const ret = encodeURIComponent(location.pathname + location.search);
    navigate(`/retail/${slug}/chat?return=${ret}`);
  };

  // Phase 3L.1 Pass B — tab-close / navigation warning when the cart has
  // items. We only attach the listener while a cart is non-empty so we
  // don't pester customers on the landing screen.
  useEffect(() => {
    const itemCount = (cart?.items || []).length;
    if (itemCount === 0) return;
    const handler = (e) => {
      // Stripe Production Cutover (Feb 2026) — suppress the "Leave site?"
      // prompt for legitimate outbound redirects to Stripe Hosted Checkout.
      // CheckoutModal sets this flag immediately before
      // `window.location.href = sess.checkout_url`, so the customer isn't
      // prompted to abandon a payment they explicitly initiated.
      if (window.__rudrasAllowUnload) return;
      e.preventDefault();
      // Modern browsers ignore the message text but still gate the close.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [cart]);

  // Kiosk-style state machine:
  //  "start"     → branching screen: Chat with us OR Start Sales Order.
  //  "welcome"   → name/phone/order-type/table modal.
  //  "menu"      → menu grid + persistent cart.
  //  "confirmed" → order-confirmed modal with receipt.
  const [phase, setPhase] = useState("start");
  const [customer, setCustomer] = useState(() => loadCustomer(slug));
  // Phase 1 (Modal Reliability) — in-progress WelcomeModal draft, kept in a
  // ref (not state) so updates on every keystroke don't re-render this
  // page. Folded into `initial` below on the next mount of WelcomeModal so
  // a deliberate close (Escape / Back) + reopen doesn't lose what the
  // customer already typed but never submitted.
  const welcomeDraftRef = useRef(null);
  // PR #41 hardening (Phase 2 — validated address as session context) —
  // the `/recognize` result (customer + saved addresses + coupon) used
  // to be trapped entirely inside WelcomeModal's local state and lost on
  // unmount. Lifting it here means a remount (Back, then Start again)
  // restores the Welcome-back banner + saved-address picker instantly
  // instead of re-running the debounced network round trip. A ref, not
  // state — recognition can fire many times per keystroke-settle and
  // must never force this page to re-render.
  const recognizedRef = useRef(null);
  // Customer Favorites (Aug 2026) — the just-completed OTP proof,
  // lifted from WelcomeModal the SAME way `recognizedRef` already is
  // (a ref, not state, since it can update many times per keystroke-
  // settle inside the modal and must never force this page to
  // re-render).
  //
  // Merge-blocker correction (Aug 2026) — this WAS deliberately never
  // persisted, on the theory that it should follow `orderAccessKey`'s
  // established security posture. That theory was backwards:
  // `orderAccessKey` itself IS persisted to `sessionStorage` (see its
  // own docstring in `retailSessionKeys.js`) specifically so a
  // same-tab refresh doesn't force a whole new OTP round trip — only a
  // genuinely new session (tab/browser closed) does. Losing this proof
  // on every refresh silently broke the approved requirement that a
  // verified member's Favorites survive a normal page refresh without
  // a second OTP. Corrected to follow the SAME pattern `orderAccessKey`
  // already established: persisted via `saveVerifiedProof`/
  // `loadVerifiedProof` (sessionStorage, slug-scoped, never
  // localStorage), and independently re-validated server-side on every
  // restore via the restoration effect below — the stored value alone
  // grants nothing; `POST /recognize` re-checks the underlying OTP
  // row's scope and freshness exactly as a live verification would.
  const verifiedProofRef = useRef(null);
  // Loyalty checkout no-request fix (Sep 2026) — the member's completed
  // OTP proof, as reactive state, for the loyalty surface only.
  //
  // The ref above is read synchronously by a dozen callbacks and stays
  // exactly as it was. What it cannot do is cause a render: writing
  // `verifiedProofRef.current` is invisible to React, so the only thing
  // that used to propagate a new proof downward was the `setVerified(true)`
  // written beside it — and `verified` is ALSO set true by the safe,
  // non-OTP recognition path below (`onSafeRecognized`), which
  // deliberately establishes no proof at all. Two consequences, both
  // confirmed in staging:
  //
  //   * `verified` was true while `verifiedProofRef.current` was null,
  //     so `verified ? verifiedProofRef.current : null` handed checkout
  //     a null proof for exactly the recognized-member case;
  //   * once `verified` was already true, `setVerified(true)` was a
  //     no-op React bails out on, so a proof arriving afterwards could
  //     not trigger or propagate the render that would deliver it.
  //
  // The result was a checkout that issued no loyalty request at all —
  // no panel, nothing in the network log, no error. Holding the proof
  // in state makes every write and every clear propagate on its own,
  // independently of `verified`, which goes back to meaning only what
  // its own comment says.
  //
  // It is a THIRD, narrower consumer — never a widening of the ref
  // above. `verifiedProofRef` is the Favorites authorization boundary
  // and `PastOrdersLookup`'s `order_lookup` proof is deliberately kept
  // out of it (`retail_customer_favorites_proof_transfer.test.js`
  // asserts that separation); this state may hold that proof because
  // all it authorizes is showing the member their own points, which
  // the backend independently re-proves on every call.
  const [verifiedProof, setVerifiedProof] = useState(null);
  // Final Operator-Validation Prerequisites correction (Aug 2026,
  // Product Owner final OTP decision) — the SAFE (non-OTP) counterpart
  // to `verifiedProofRef` above. Holds `{ phone, email }` for a
  // Dine-In/Takeout walk-in customer the SAFE, pre-OTP lookup
  // confirmed is an active member — NOT an OTP proof, and never used
  // for anything past-orders-related (that stays exclusively gated by
  // `verifiedProofRef`/a completed OTP). A ref, not state, for the
  // same reason `verifiedProofRef` is: it can update many times per
  // keystroke-settle inside WelcomeModal and must never force this
  // page to re-render. Deliberately NOT persisted to sessionStorage —
  // unlike an OTP proof, there is no server-issued token to re-
  // validate on restore, so a refresh simply re-derives it the same
  // way it was derived the first time (retyping the phone number), the
  // same posture the pre-existing `checkSafeStatus` pre-check already
  // has.
  const safeProofRef = useRef(null);
  // A single helper both `onToggleFavorite`/`onRemoveFavorite` below
  // use — prefers a real OTP proof when one exists (it is strictly
  // more capable), falling back to the safe, non-OTP proof otherwise.
  // `useRetailFavorites` itself treats the two shapes identically
  // (`{ token, ... }` vs. `{ safe: true, phone, email }`) via its own
  // `_hasProof`/`_proofBody` helpers.
  const currentFavoritesProof = () =>
    verifiedProofRef.current || (safeProofRef.current ? { safe: true, ...safeProofRef.current } : null);
  // Merge-blocker correction (Aug 2026, round 3) — the ONE
  // authoritative, server-issued deadline this proof stops being
  // honorable, captured verbatim from `recognize()`'s own
  // `proof_expires_at` (both on a live verification and on a restore)
  // and NEVER derived from `verifiedAt`/anything else the browser
  // holds. `endMemberSession()`'s scheduling effect below reads this,
  // never `sessionStorage`, so tampering with the persisted proof's
  // `verifiedAt` field cannot extend a session — the value that
  // actually matters only ever comes from a fresh network response.
  const verifiedProofExpiresAtRef = useRef(null);
  // Merge-blocker correction (Aug 2026) — true once this page has
  // attempted (successfully or not) to restore a persisted verified
  // proof from a prior visit this browser session. Guards the
  // restoration effect below so it runs at most once per mount, and
  // lets it wait for `catalog?.id` to be available (the proof's scope
  // check is catalog-bound) without re-firing on every unrelated
  // catalog-derived render.
  const [proofRestoreAttempted, setProofRestoreAttempted] = useState(false);
  // "locked" (default) — no confirmed active membership yet this
  // session; "live" — set only once `onWelcomeSubmit` below confirms
  // an active membership from the just-completed recognize/enroll
  // flow. Real state (not a ref): drives the star/tab UI, which must
  // re-render once membership status is known.
  const [favoriteAccess, setFavoriteAccess] = useState("locked");
  // Walk-in customer recognition/verification correction (Aug 2026) —
  // "has this customer completed OTP verification at all this
  // session", kept as its own piece of REAL state (mirrors
  // `verifiedProofRef`, promoted so the Favorites tab/product-card UI
  // can distinguish "not yet verified" from "verified but not a
  // member" — two states `favoriteAccess` alone ("locked"/"live")
  // cannot tell apart, but the UI must show different copy/actions for
  // each). Set alongside every place `verifiedProofRef.current` itself
  // is set or cleared.
  const [verified, setVerified] = useState(false);
  // Restaurant Sales Assistant Unification (Mar 2026) — if the chat sent
  // the customer back here with `?orderType=<mode>`, preselect that
  // mode for the welcome modal and jump straight to it. Lets the
  // Service-mode chips in chat sidebar feel one-tap.
  //
  // Restaurant Pilot Readiness (Mar 2026) — QR-code entry. URL params
  //   ?table=<n>     captures the table number on the customer record.
  //   ?mode=<m>      alias for ?orderType=.
  //   ?kiosk=1       enables tablet/kiosk mode (hides outer nav).
  const _qs = (() => { try { return new URLSearchParams(location.search); } catch (_e) { return null; } })();
  const _modeFromUrl = (_qs?.get("orderType") || _qs?.get("mode") || "").toLowerCase();
  const _tableFromUrl = (_qs?.get("table") || "").trim();
  const _kioskMode = (_qs?.get("kiosk") || "").trim() === "1";
  // Marketing P0-4 — remember tablet mode for this slug so checkout can
  // send the `client_mode: "kiosk"` reporting hint. Changes nothing else.
  useEffect(() => {
    if (_kioskMode) saveClientMode(slug, "kiosk");
  }, [_kioskMode, slug]);
  const [orderType, setOrderType] = useState(
    _modeFromUrl || (loadCustomer(slug)?.orderType) || "dine_in"
  );
  // Hospitality H1 (Sep 2026) — hotel/motel referral. The one-time
  // `?host_ref=` token is captured once (the hook strips it from the URL
  // after binding it server-side); from then on the server alone says
  // whether this cart session is hotel-referred. When it is, the order is
  // Local Delivery to the hotel — enforced by the server at checkout,
  // mirrored here only so the guest never sees an address form.
  const [_hostRefToken] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("host_ref") || ""; } catch (_e) { return ""; }
  });
  const hostReferral = useHostReferral({ slug, sessionId, token: _hostRefToken });
  const hostDestination = hostReferral.context;
  useEffect(() => {
    if (hostDestination) setOrderType("delivery");
  }, [hostDestination]);
  // The order type every child sees. Derived (not only set once) so a
  // hotel context that resolves after the guest already picked another
  // mode still wins on the very next render, whatever state lags behind.
  const effectiveOrderType = hostDestination ? "delivery" : orderType;
  useEffect(() => {
    // If table number arrived via QR, persist it on the customer record so
    // subsequent welcome-modal submits + checkout retain it without the
    // operator having to ask.
    if (_tableFromUrl) {
      try {
        const cur = loadCustomer(slug) || {};
        const updated = { ...cur, table_number: _tableFromUrl, tableNumber: _tableFromUrl, last_seen_at: new Date().toISOString() };
        sessionStorage.setItem(customerKey(slug), JSON.stringify(updated));
        setCustomer(updated);
      } catch (_e) { /* noop */ }
    }
    if (_modeFromUrl) {
      setPhase("welcome");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  // Phase 3L additions
  const [chatOpen, setChatOpen] = useState(false);
  const [infoProduct, setInfoProduct] = useState(null);
  const [confirmNewOrder, setConfirmNewOrder] = useState(false);

  // ----- derived ----------------------------------------------------------
  const tenant = catalogData?.tenant;
  const catalog = catalogData?.catalog;
  const categories = useMemo(() => (catalogData?.categories || []).map((c) => ({
    ...c,
    products: (c.products || []).map((p) => ({ ...p, _img: cleanImageUrl(p.image_url) })),
  })), [catalogData]);
  const currency = catalog?.currency || "USD";

  // ----- Sticky Search (Sep 2026) ----------------------------------------
  // Declared ABOVE `useRetailFavorites` and the cart hook, and holding
  // ALL of its state here in the page, so that typing in Search only
  // ever re-renders — it never remounts either hook, never refetches the
  // catalog, and therefore can never disturb the cart or a member's
  // favourite state. That is the specific regression this ordering
  // exists to prevent, and it is covered by a dedicated test.
  //
  // The BACKEND decides the mode via `catalog.search_mode`: "client"
  // when the whole catalog is in this payload (instant, no round trip),
  // "server" when the payload is a capped page and suggestions must
  // query the full catalog so nothing is unreachable.
  const searchEnabled = catalog?.attribute_config?.search_enabled !== false;
  // Customer-selectable spice (Sep 2026, restored) — the catalog's own
  // existing "Spice level" switch, resolved server-side
  // (`product_attributes.resolve_attribute_config`), so it is always an
  // explicit boolean here. Off means no card offers a selector however
  // its products are configured; a payload that predates the field
  // falls back to the product-level gate, which renders nothing for a
  // product with no configured level anyway.
  const spiceEnabled = catalog?.attribute_config?.spice_enabled !== false;
  const searchMode = catalog?.search_mode === "server" ? "server" : "client";

  // ----- Product Family scope (Sep 2026) --------------------------------
  // Declared ABOVE search for the same reason search is declared above
  // the cart: the family scopes what search sees. The tab row is built
  // from the backend's own catalog-wide family facets when the catalog
  // has the capability switched on, and falls back to the families
  // actually present in the loaded payload — so the tabs can never
  // offer a family that has no eligible product behind it.
  const [familyFilter, setFamilyFilter] = useState(FAMILY_ALL);
  const familyEnabled = !!catalog?.attribute_config?.family_enabled;
  const familyTabs = useMemo(() => {
    if (!familyEnabled) return [];
    const fromFacets = (catalogData?.facets?.families || []).map((f) => f.value);
    const source = fromFacets.length ? fromFacets : familiesFromCategories(categories);
    return buildFamilyTabs(source);
  }, [familyEnabled, catalogData, categories]);
  // The catalog can reload (or a tenant can withdraw the last product
  // of a family) while a family tab is selected. Reconciling here means
  // the Sales Order can never be left filtered to a family that no
  // longer exists, which would render an empty menu with no visible
  // reason for it.
  const activeFamily = resolveActiveFamily(familyFilter, familyTabs);
  useEffect(() => {
    if (activeFamily !== familyFilter) setFamilyFilter(activeFamily);
  }, [activeFamily, familyFilter]);
  // The catalog, narrowed to the active family. Every downstream
  // surface — the category row, the menu sections, the search box —
  // reads THIS rather than `categories`, which is what makes the
  // family scope apply to all three from one place. Under ALL it is
  // `categories` itself, so nothing changes for a catalog that has
  // never used Product Family.
  const familyCategories = useMemo(
    () => filterCategoriesByFamily(categories, activeFamily),
    [categories, activeFamily],
  );

  const allProducts = useMemo(
    () => familyCategories.flatMap((c) => c.products || []),
    [familyCategories],
  );
  const search = useProductSearch({
    slug, catalogId: catalog?.id, linkSlug,
    mode: searchMode, products: allProducts,
    // Server mode searches the FULL collection, so the family has to
    // travel with the query or a capped grocery catalog would suggest
    // a Grocery product while RESTAURANT is selected. The client mode
    // needs no equivalent — it is already searching `allProducts`,
    // which is family-scoped above.
    family: activeFamily === FAMILY_ALL ? null : activeFamily,
  });
  // Brand / Product Family browsing is ALWAYS served by the backend, in
  // both modes and deliberately: filtering the in-memory page would
  // silently hide products past the browse cap.
  const facetBrowse = useFacetBrowse({
    slug, catalogId: catalog?.id, linkSlug, facet: search.facet,
    family: activeFamily === FAMILY_ALL ? null : activeFamily,
  });
  // Sales Order filters (Oct 2026). Brand and Size narrow the family's
  // INDIVIDUAL products (one card per exact product — never a grouped
  // card), served by the backend over the complete catalog. Offered only
  // for a catalog that switched Brand or Package size on; a restaurant
  // menu makes no request and shows no filter.
  const filters = useCatalogFilters({
    slug, catalogId: catalog?.id, linkSlug,
    family: activeFamily === FAMILY_ALL ? null : activeFamily,
    query: search.trimmed,
    enabled: !!(catalog?.attribute_config?.brand_enabled || catalog?.attribute_config?.pack_enabled),
  });
  // A product the customer picked from Search that is NOT in the loaded
  // browse page — fetched authoritatively by its immutable id.
  const selected = useSelectedProduct({ slug, catalogId: catalog?.id, linkSlug });
  // Bumped every time a card should be scrolled to and highlighted. A
  // nonce rather than a bare id so picking the SAME product twice still
  // re-reveals it.
  const [revealTarget, setRevealTarget] = useState(null);

  // Choosing a Product suggestion. The ordering rules live in ONE
  // shared factory (`createProductSelectionHandler`) that this page and
  // its acceptance tests both run, so they cannot drift apart.
  const handleSelectProduct = useMemo(
    () => createProductSelectionHandler({
      allProducts, search, selected, onReveal: setRevealTarget,
    }),
    [allProducts, search, selected],
  );

  // Leaving the focused search result restores the catalog the customer
  // was browsing — the same single action that clears a facet chip.
  const clearSelectedProduct = React.useCallback(() => {
    selected.clear();
    setRevealTarget(null);
  }, [selected]);

  // Switching Product Family is ONE predictable action, exactly like
  // tapping a category: everything scoped to the family being left is
  // released first — the text query, any Brand/Family chip, a focused
  // off-page search result, and a pending reveal — so the customer can
  // never land on a filtered-by-something-else view of the new family,
  // or on a card that does not belong to it. The category row and the
  // active-pill highlight reconcile themselves inside `KioskShell`.
  const handleFamilyChange = React.useCallback((next) => {
    setFamilyFilter(next || FAMILY_ALL);
    search.clear();
    selected.clear();
    setRevealTarget(null);
  }, [search, selected]);

  // Customer Favorites (Aug 2026). The favorites API/UI only ever
  // applies when this tenant has Membership turned on at all — for a
  // tenant with Membership disabled, Favorites can never work
  // (there's no way to become a member), so no star/tab is shown at
  // all rather than presenting a permanently-unusable affordance.
  const membershipEnabled = !!tenant?.membership_config?.enabled;
  // Merge-blocker correction (Aug 2026, round 3) — `endMemberSession`
  // itself is declared further below (it needs `favorites.reset`, so
  // it can't come first) but this hook needs to be ABLE to call it the
  // moment any Favorites call comes back 401/403 (proof rejected
  // server-side — the one signal that must clear a rendered member
  // session immediately, not wait for a timer). A ref indirection
  // breaks the circular ordering: this callback is stable forever, and
  // always reads whichever `endMemberSession` is current by the time
  // it actually fires.
  const endMemberSessionRef = useRef(null);
  const favorites = useRetailFavorites({
    slug, catalogId: catalog?.id,
    onAuthRejected: () => endMemberSessionRef.current && endMemberSessionRef.current(),
  });
  // Favorites tab prepended as the FIRST category — before every real
  // category — only when Membership is enabled for this tenant.
  // `isFavoritesTab` is what `KioskShell` uses to render
  // `FavoritesSection` instead of an ordinary `MenuSection` for this
  // one synthetic entry.
  // What the menu actually renders. Three states, in priority order:
  //   1. a Brand / Product Family chip is applied -> the backend's own
  //      complete, paginated result set, shown as one titled group;
  //   2. a text query is active -> the same categories the customer
  //      already understands, narrowed in place (never flattened);
  //   3. neither -> the catalog exactly as it loads today.
  const visibleCategories = useMemo(() => {
    // A product opened from Search that the browse page never contained.
    // Rendered through the ORDINARY card path, so Brand, pack size,
    // Vegan, Spice, the authoritative price, Add-to-cart and the
    // Favorites star all work exactly as they do anywhere else.
    if (selected.product) {
      return [{
        name: "Search result",
        isSearchResult: true,
        products: [{ ...selected.product, _img: cleanImageUrl(selected.product.image_url) }],
      }];
    }
    if (search.facet) {
      return [{
        name: search.facet.value,
        products: (facetBrowse.items || []).map((p) => ({
          ...p, _img: cleanImageUrl(p.image_url),
        })),
      }];
    }
    // Brand / Size / Category filters: the server's filtered page of
    // individual products (search text included), shown as one group.
    if (filters.active) {
      return [{
        name: filters.selection.category || "Filtered products",
        products: (filters.items || []).map((p) => ({ ...p, _img: cleanImageUrl(p.image_url) })),
      }];
    }
    // Pipeline order (Sep 2026): the backend has already applied
    // tenant/catalog scope and the active/available rules; the Product
    // Family filter narrowed that into `familyCategories`; the text
    // query narrows it again here. Search therefore operates strictly
    // WITHIN the family the customer is on, and the tenant's
    // configured category sequence survives both steps because neither
    // one re-sorts — they only remove.
    if (search.active) return filterCategories(familyCategories, search.trimmed, null);
    return familyCategories;
  }, [selected.product, search.facet, search.active, search.trimmed,
      familyCategories, facetBrowse.items, filters.active, filters.items, filters.selection]);

  const categoriesForShell = useMemo(() => {
    if (!membershipEnabled) return visibleCategories;
    // The Favorites tab is never filtered out — a customer searching for
    // "cake" must not find their saved Favorites emptied.
    return [{ name: "Favorites", isFavoritesTab: true, products: [] }, ...visibleCategories];
  }, [visibleCategories, membershipEnabled]);

  // Merge-blocker correction (Aug 2026) — extracted from an inline JSX
  // prop so the restoration effect below can call the exact same
  // recognition round trip WelcomeModal uses, without needing
  // WelcomeModal mounted. Behavior is byte-for-byte unchanged from the
  // prior inline version.
  const recognizeCustomer = React.useCallback(async (arg) => {
    const s =
      (tenant && (tenant.chatbot_slug || tenant.slug)) ||
      (catalog && (catalog.chatbot_slug || catalog.slug));
    if (!s) return null;
    const body = typeof arg === "string" ? { phone: arg } : (arg || {});
    const { data } = await api.post(`/public/retail/${s}/recognize`, {
      phone: body.phone || null,
      email: body.email || null,
      catalog_id: catalog?.id,
      checkout_attempt_token: body.checkout_attempt_token || null,
      destination_channel: body.destination_channel || null,
      destination_value: body.destination_value || null,
    });
    return data;
  }, [tenant, catalog]);

  // Walk-in customer recognition/verification correction (Aug 2026) —
  // the SAFE, pre-OTP counterpart to `recognizeCustomer` above. Never
  // sends or requires an OTP, and returns only two booleans (see
  // `services/customers.py::get_safe_recognition_signals`'s own
  // docstring for the full non-PII contract) — used SOLELY to let the
  // Welcome screen pre-check + disable the SMS-consent/Membership
  // checkboxes for a genuinely returning customer.
  const checkSafeStatus = React.useCallback(async (arg) => {
    const s =
      (tenant && (tenant.chatbot_slug || tenant.slug)) ||
      (catalog && (catalog.chatbot_slug || catalog.slug));
    if (!s) return null;
    const body = arg || {};
    const { data } = await api.post(`/public/retail/${s}/customer-status`, {
      phone: body.phone || null,
      email: body.email || null,
      catalog_id: catalog?.id,
    });
    return data;
  }, [tenant, catalog]);

  // Merge-blocker correction (Aug 2026) — Favorites refresh continuity.
  // Restores a verified member's session across a normal browser
  // refresh WITHOUT a duplicate OTP and WITHOUT trusting anything the
  // browser says about itself. Runs once catalog data is available
  // (the stored proof's scope check is catalog-bound) and only when
  // this tenant has Membership enabled at all (Favorites is the only
  // thing this restores — general "welcome back" recognition is
  // unaffected and out of scope for this correction).
  //
  // Security model: `loadVerifiedProof` returns an opaque token this
  // page cannot itself interpret as authorization for anything — it is
  // handed straight to the SAME `recognize()` call a live verification
  // uses, which re-derives customer + membership status entirely
  // server-side (`load_verified_customer` re-reads the underlying OTP
  // row, scoped to this exact company_id/catalog_id/destination, and
  // enforces the same freshness window a live check would). A
  // stored proof that is expired, revoked, was issued for a different
  // tenant/catalog, or was tampered with restores nothing — the
  // backend returns `{customer: null, member: null}` exactly as for an
  // unverified caller (see `routes/commerce.py::recognize`), and this
  // effect responds by clearing the stale value and leaving Favorites
  // hidden. No OTP is ever issued by this path — `recognize()` only
  // ever re-validates an ALREADY-verified proof, never sends a code.
  useEffect(() => {
    if (!membershipEnabled) return;
    if (!catalog?.id) return;
    if (proofRestoreAttempted) return;
    if (verifiedProofRef.current) {
      // A live verification already completed this render cycle
      // (e.g. WelcomeModal submitted before this effect got a chance
      // to run) — nothing to restore, and restoring now would only
      // risk clobbering fresher state with a stale one.
      setProofRestoreAttempted(true);
      return;
    }
    const stored = loadVerifiedProof(slug);
    if (!stored) {
      setProofRestoreAttempted(true);
      return;
    }
    setProofRestoreAttempted(true);
    let cancelled = false;
    (async () => {
      let result = null;
      try {
        // Security-audit correction (Aug 2026, round 2) — `stored`
        // never carries a `destinationValue` (see `saveVerifiedProof`'s
        // own docstring: that would be raw customer PII sitting in
        // browser storage). Omitting it here is exactly what makes the
        // backend's token-only restore path apply — `load_verified_customer`
        // resolves the customer from a value it already cached
        // server-side against this same token during the ORIGINAL live
        // verification, not from anything resupplied here.
        result = await recognizeCustomer({
          checkout_attempt_token: stored.token,
          destination_channel: stored.channel,
        });
      } catch (_e) {
        // Network/unexpected failure — fail closed (Favorites stays
        // hidden for this attempt) without discarding the stored
        // token, since the token itself may still be perfectly valid
        // and worth another try on the next mount.
        return;
      }
      if (cancelled) return;
      if (verifiedProofRef.current) return; // a fresher proof arrived meanwhile
      if (!result?.customer) {
        // Expired, revoked, tenant/catalog-mismatched, or otherwise
        // unusable — the backend already told us this exactly as it
        // would an unverified caller. `endMemberSession` (via the ref
        // indirection — it isn't declared until later in this
        // component) clears the stale value AND any in-memory display
        // state, so we never keep re-attempting a proof that will
        // never succeed, and never leave anything rendered from it.
        endMemberSessionRef.current && endMemberSessionRef.current();
        return;
      }
      recognizedRef.current = result;
      verifiedProofRef.current = stored;
      setVerifiedProof(stored);
      setVerified(true);
      // Security-audit correction (Aug 2026, round 3) — the ONE
      // authoritative deadline for this restored proof, taken verbatim
      // from the backend's own response. A restore always inherits the
      // ORIGINAL remaining lifetime here (recognize() re-reads the same
      // immutable `verified_at`), never a fresh 15-minute grant.
      verifiedProofExpiresAtRef.current = result?.proof_expires_at
        ? new Date(result.proof_expires_at).getTime() : null;
      const isActiveMember = result?.member?.status === "active";
      setFavoriteAccess(isActiveMember ? "live" : "locked");
      if (isActiveMember) {
        favorites.loadFavorites(stored);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipEnabled, catalog?.id, slug, proofRestoreAttempted, recognizeCustomer]);

  // Shared-kiosk correction (Aug 2026) — the single place every "this
  // verified member's session is over" event below routes through.
  // Clears the in-memory display state (Favorites tab, filled stars,
  // membership banner) AND the persisted sessionStorage proof, but
  // deliberately does NOT touch the cart, the current order-in-progress,
  // or `customer`/`orderType` — those belong to the SAME physical
  // transaction and ending the Favorites/membership session must never
  // silently discard an order someone is mid-way through placing.
  // Never sends an OTP and never touches server-side Favorites or
  // membership records — this only forgets what THIS browser tab is
  // currently displaying.
  const endMemberSession = React.useCallback(() => {
    verifiedProofRef.current = null;
    setVerifiedProof(null);
    verifiedProofExpiresAtRef.current = null;
    recognizedRef.current = null;
    safeProofRef.current = null;
    clearVerifiedProof(slug);
    setFavoriteAccess("locked");
    setVerified(false);
    favorites.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  endMemberSessionRef.current = endMemberSession;

  // Shared-kiosk correction (Aug 2026) — Rudras-AI runs on shared
  // Samsung tablet kiosks as well as personal devices. Restoring a
  // verified member's Favorites across a REFRESH (the fix above) would
  // otherwise also mean a member's session sits live in memory
  // indefinitely if the tab is simply left open and idle — the next
  // customer to pick up the SAME tablet could see the prior customer's
  // filled stars and Favorites tab without ever refreshing at all.
  // Security-audit correction (Aug 2026, round 3) — this is the EARLIER
  // of two distinct deadlines (see `MEMBER_SESSION_IDLE_MS`'s own
  // comment); the separate absolute-expiry effect just below it is the
  // one actually tied to the backend's proof lifetime and is NEVER
  // reset by activity. Only runs while there is something to protect
  // (`favoriteAccess === "live"`); a locked/no-membership tab has no
  // per-customer data to expire. Any click/keypress/touch anywhere on
  // the page resets THIS clock only — this is activity-based, not
  // merely a fixed timer from verification.
  useEffect(() => {
    if (favoriteAccess !== "live") return;
    let timer = null;
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        endMemberSession();
      }, MEMBER_SESSION_IDLE_MS);
    };
    const onActivity = () => arm();
    const events = ["click", "keydown", "touchstart", "pointerdown"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    arm();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, [favoriteAccess, endMemberSession]);

  // Security-audit correction (Aug 2026, round 3) — credential-lifetime
  // alignment. The idle timer above is activity-RESET and therefore
  // cannot, on its own, guarantee a rendered member session ends by the
  // backend's own ABSOLUTE proof expiry (`VERIFIED_TOKEN_FRESHNESS_SECONDS`,
  // 15 minutes from `verified_at`, never extended by activity server-side).
  // Without this, browser activity could keep already-rendered Favorites
  // visible well past the moment the backend would reject a real API
  // call using that same proof. This effect is a SEPARATE, non-resettable
  // deadline sourced ONLY from `verifiedProofExpiresAtRef` — populated
  // exclusively from `recognize()`'s own `proof_expires_at` on every
  // live verification AND every restore (both call the SAME backend
  // path, so a restore always inherits the ORIGINAL remaining lifetime,
  // never a fresh 15 minutes). Click/keydown/touch/pointer activity
  // re-arms the idle timer above but never touches this effect or its
  // ref — the absolute deadline cannot be pushed out by anything the
  // browser does. A missing/malformed expiry (should not happen given
  // `recognize()` always returns one for a valid proof, but defensively
  // handled) fails CLOSED — ends the session immediately rather than
  // granting unbounded access.
  useEffect(() => {
    if (favoriteAccess !== "live") return;
    // Final Operator-Validation Prerequisites correction (Aug 2026) —
    // this deadline is specifically about a backend-issued OTP proof's
    // fixed freshness window (`verifiedProofExpiresAtRef`, populated
    // exclusively by the OTP path — see its own docstring). The SAFE,
    // non-OTP walk-in path (`safeProofRef`) has no such proof or
    // expiry to enforce — it's re-derived fresh from a phone/email
    // match on every debounced lookup, not a stored credential with a
    // lifetime. Without this guard, a walk-in member's session was
    // killed the INSTANT it went live: `favoriteAccess` became "live"
    // with no OTP proof ever issued, so `verifiedProofExpiresAtRef`
    // was never populated, and the "missing expiry fails closed" rule
    // below (correctly protective for the OTP path) fired immediately.
    if (!verifiedProofRef.current) return;
    const expiresAtMs = verifiedProofExpiresAtRef.current;
    if (!expiresAtMs || Number.isNaN(expiresAtMs)) {
      endMemberSession();
      return;
    }
    const msRemaining = expiresAtMs - Date.now();
    if (msRemaining <= 0) {
      endMemberSession();
      return;
    }
    const timer = setTimeout(() => { endMemberSession(); }, msRemaining);
    return () => clearTimeout(timer);
  }, [favoriteAccess, endMemberSession]);

  // Shared-kiosk correction (Aug 2026) — a tenant/store slug change
  // (e.g. this exact tab is repointed to a different storefront via URL
  // edit or an in-app navigation) OR a catalog change (a different
  // link-profile catalog under the SAME tenant) must never let a PRIOR
  // tenant's/catalog's live-in-memory Favorites/membership state keep
  // showing under the new one — Favorites are catalog-scoped server
  // data, so leaving stale `favorites.items` displayed after a catalog
  // switch would be a genuine data-correctness bug, not just a stale
  // UI. `loadVerifiedProof` is already slug-scoped so a different
  // tenant's STORED proof was never reachable here — this additionally
  // covers the in-memory (`favoriteAccess`/`favorites`/refs) state a
  // mere param change wouldn't otherwise reset, and re-arms the
  // restoration effect above so the NEW tenant/catalog's own stored
  // proof (if any) gets a fresh, correctly-scoped attempt.
  const mountedScopeRef = useRef({ slug, catalogId: catalog?.id || null });
  useEffect(() => {
    const currCatalogId = catalog?.id || null;
    const prev = mountedScopeRef.current;
    const slugChanged = prev.slug !== slug;
    // Only a REAL catalog id changing to a DIFFERENT real catalog id
    // counts — the very first mount transitions from "not loaded yet"
    // (null) to a real id, which is not a change to react to here (the
    // restoration effect above already handles that natural startup
    // sequencing on its own).
    const catalogChanged = !!prev.catalogId && !!currCatalogId && prev.catalogId !== currCatalogId;
    if (!slugChanged && !catalogChanged) {
      if (currCatalogId !== prev.catalogId) mountedScopeRef.current = { slug, catalogId: currCatalogId };
      return;
    }
    mountedScopeRef.current = { slug, catalogId: currCatalogId };
    verifiedProofRef.current = null;
    setVerifiedProof(null);
    recognizedRef.current = null;
    safeProofRef.current = null;
    setFavoriteAccess("locked");
    setVerified(false);
    favorites.reset();
    setProofRestoreAttempted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, catalog?.id]);

  // Phase 3L.6 / Iter41 — per-catalog Chat Assistant toggle. Defaults to
  // true when the field is missing (live beta behavior). When false, every
  // chat entry surface is hidden from the storefront.
  const chatEnabled = catalog?.chat_assistant_enabled !== false;
  // PR #41 hardening — Phase 7 (AI Assistant context handoff contract).
  // NOT AI add-on selling — this only persists the stable context shape
  // a future assistant integration would consume (see
  // lib/aiAssistantContext.js). Existing chat/assistant behavior is
  // unchanged; nothing currently reads this key.
  useEffect(() => {
    saveAssistantContext(slug, buildAssistantContext({
      slug, catalogId: catalog?.id, catalogName: catalog?.name,
      linkSlug, customer, orderType, cart,
    }));
  }, [slug, catalog?.id, catalog?.name, linkSlug, customer, orderType, cart]);
  // Cart-line identity (Sep 2026, restored) — indexed by
  // `(product_id, spice_level)` so a card whose selection is Hot steps
  // the Hot line and never the Low one. The plain product-id map is
  // kept alongside it, because the consumers that have no spice
  // selection to reason about (search suggestions, the offpage product
  // view) still ask only "is this product in the cart at all?".
  const itemsInCart = useMemo(() => {
    const m = new Map();
    for (const it of cart?.items || []) m.set(it.product_id, it);
    return m;
  }, [cart]);
  const cartLines = useMemo(() => indexCartItems(cart?.items || []), [cart]);
  const cartCount = (cart?.items || []).reduce((s, it) => s + (it.quantity || 0), 0);

  // Phase 3L — order modes the operator enabled for THIS catalog. Anchors
  // the welcome modal's order-type picker + checkout validation.
  const { orderModes, enabledModeList, labelFor } = useRetailOrderModes(catalog);

  // ----- gating ------------------------------------------------------------
  // Restaurant Pilot Readiness (Mar 2026) — kiosk mode flag. Hook MUST
  // run BEFORE any early return so React's hook-order contract holds.
  // When `?kiosk=1` is set, add `kiosk-mode` on html/body so the kiosk
  // CSS sheet hides outer nav + locks scroll.
  useEffect(() => {
    if (_kioskMode) {
      document.documentElement.classList.add("kiosk-mode");
      document.body.classList.add("kiosk-mode");
    }
    return () => {
      document.documentElement.classList.remove("kiosk-mode");
      document.body.classList.remove("kiosk-mode");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <FullPageLoader />;
  if (gateError) return <FullPageError gateError={gateError} />;

  const onStart = () => {
    // First-time visit: ensure the welcome modal lands on the FIRST enabled mode.
    const defaultMode = enabledModeList[0] || "dine_in";
    setOrderType(defaultMode);
    setPhase("welcome");
  };

  // Restaurant Pilot Readiness (Mar 2026) — service-mode tile tap.
  // Skip the landing screen and pop the welcome modal pre-set to the
  // chosen mode. Falls back to onStart when mode isn't enabled.
  const onStartMode = (mode) => {
    if (hostDestination) { setOrderType("delivery"); setPhase("welcome"); return; }
    const m = (mode || "").toLowerCase();
    if (enabledModeList.includes(m)) {
      setOrderType(m);
      setPhase("welcome");
    } else {
      onStart();
    }
  };

  // Restaurant Pilot Readiness (Mar 2026) — Continue Order / Start New Order
  // affordance shown when a recent saved customer is present.
  const onResume = (saved) => {
    if (saved) {
      setCustomer(saved);
      setOrderType(hostDestination ? "delivery" : (saved.orderType || enabledModeList[0] || "dine_in").toLowerCase());
      // If we already have a cart on the server side (same sessionId), go
      // straight to menu; otherwise the welcome modal still lets them edit.
      if ((cart?.items || []).length > 0) setPhase("menu");
      else setPhase("welcome");
    } else {
      onStart();
    }
  };
  const onForget = () => {
    startNewOrder();
  };

  const requestNewOrder = () => {
    if ((cart?.items || []).length > 0) setConfirmNewOrder(true);
    else startNewOrder();
  };

  const onWelcomeSubmit = (next) => {
    setCustomer(next);
    saveCustomer(slug, next);
    setOrderType(hostDestination ? "delivery" : next.orderType);
    // Customer Favorites (Aug 2026) — `recognizedRef` already reflects
    // the latest membership status by the time the modal submits (see
    // WelcomeModal's own `onRecognized` calls, fired after both the
    // initial recognize() AND a successful enroll()). Read it fresh
    // here rather than trusting anything cached from an earlier
    // render — membership status can only ever come from that
    // server-verified response, never inferred client-side.
    const isActiveMember = recognizedRef.current?.member?.status === "active";
    // Final Operator-Validation Prerequisites correction (Aug 2026) —
    // this OTP-path check previously ALWAYS ran, unconditionally
    // overwriting whatever `onSafeRecognized` had already correctly
    // set (a walk-in active member's `favoriteAccess`/`verified`
    // would be reset right back to "locked"/false here, since
    // `recognizedRef` is never populated by the safe, non-OTP path).
    // Only apply the OTP-derived state when an OTP proof actually
    // exists this session — otherwise the safe path's own state
    // (already set live, by `onSafeRecognized`) is left exactly as it
    // is.
    if (verifiedProofRef.current) {
      setFavoriteAccess(isActiveMember ? "live" : "locked");
      if (isActiveMember) {
        favorites.loadFavorites(verifiedProofRef.current);
      } else {
        favorites.reset();
      }
    } else if (!safeProofRef.current) {
      favorites.reset();
    }
    setPhase("menu");
  };

  const onPlaced = (order) => {
    setOrderPlaced(order);
    setCheckoutOpen(false);
    setPhase("confirmed");
    // Shared-kiosk correction (Aug 2026) — a successful order is the
    // transaction's natural end point. End the verified member's
    // Favorites/membership session HERE, immediately, rather than
    // waiting for an explicit "New Order" click that a departing
    // customer (or a busy kiosk) might never make — the next person to
    // touch this tablet before that click must not see the prior
    // customer's Favorites. The receipt/order-confirmed screen itself
    // doesn't depend on membership state, so this is safe to do while
    // still showing the confirmation.
    endMemberSession();
  };

  const startNewOrder = () => {
    setOrderPlaced(null);
    setPrintOpen(false);
    setCustomer(null);
    sessionStorage.removeItem(customerKey(slug));
    // A deliberate "start new order" (or "forget me") ends this
    // browser's verified-customer continuity too, exactly like it
    // already ends the saved customer record above.
    endMemberSession();
    // Rotate the session id so the next GET /cart doesn't 404 against the
    // just-consumed cart. The old session is dead on the server anyway.
    const fresh = `r-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    sessionStorage.setItem(SESSION_KEY, fresh);
    setCart(null);
    setPhase("start");
    // Sprint 1 Go-Live (Feb 2026) — flag the imminent navigation as
    // intentional so the cart-guard `beforeunload` handler doesn't
    // trigger the "Reload site? Changes you may not be saved."
    // browser warning while we soft-reload for a clean session boot.
    try { window.__rudrasAllowUnload = true; } catch (_e) { /* noop */ }
    // Force a soft reload so the new sessionId picks up cleanly.
    window.location.reload();
  };

  // ----- view dispatch -----------------------------------------------------
  return (
    <div className={"min-h-screen bg-[#0d1f12] text-stone-100" + (_kioskMode ? " kiosk-mode" : "")} data-kiosk={_kioskMode ? "1" : "0"}>
      <HostReferralBanner context={hostDestination} error={hostReferral.error} />
      {phase === "start" && (
        <SalesOrderLanding
          tenant={tenant} catalog={catalog} slug={slug}
          onStart={hostDestination ? () => onStartMode("delivery") : onStart}
          lockedModes={hostDestination ? ["delivery"] : null}
          onStartMode={onStartMode}
          onResume={onResume}
          onForget={onForget}
          onChat={chatEnabled ? goToChat : null}
        />
      )}

      {phase === "welcome" && (
        <WelcomeModal
          tenant={tenant} slug={slug} catalog={catalog} sessionId={sessionId}
          hostDestination={hostDestination}
          // Sprint 1 Go-Live (Feb 2026) — carry the landing-tile mode
          // choice into the modal. If the customer clicked "Delivery"
          // on the landing page, the modal must open pre-selected to
          // Delivery with address fields immediately visible — they
          // must never have to click Delivery a second time.
          initial={{
            ...(customer || {}),
            ...(welcomeDraftRef.current || {}),
            orderType: effectiveOrderType || (welcomeDraftRef.current && welcomeDraftRef.current.orderType) || (customer && customer.orderType),
            recognized: recognizedRef.current || undefined,
          }}
          orderModes={orderModes}
          enabledModeList={enabledModeList}
          labelFor={labelFor}
          onCancel={() => setPhase("start")}
          onSubmit={(payload) => { welcomeDraftRef.current = null; onWelcomeSubmit(payload); }}
          onDraftChange={(draft) => { welcomeDraftRef.current = draft; }}
          onRecognized={(r) => {
            recognizedRef.current = r;
            // Security-audit correction (Aug 2026, round 3) — the
            // SAME authoritative deadline captured on the live-
            // verification path (mirrors the restoration effect
            // above). `r` is `recognize()`'s full response, so
            // `proof_expires_at` is already right here — no separate
            // round trip needed.
            if (r) {
              verifiedProofExpiresAtRef.current = r.proof_expires_at
                ? new Date(r.proof_expires_at).getTime() : null;
              // Walk-in customer recognition/verification correction
              // (Aug 2026) — ROOT CAUSE FIX. Before this, `favoriteAccess`
              // was computed exactly ONCE, synchronously, inside
              // `onWelcomeSubmit` below — never revisited afterward. But
              // WelcomeModal's own OTP auto-verify effect races
              // independently of the "Start Sales Order" click: it is
              // entirely possible (and, per the confirmed staging
              // report, actually happened) for verification to settle
              // AFTER the welcome form was already submitted and the
              // customer moved on to the menu screen. `onRecognized` was
              // already firing at that later moment — it just never
              // told `favoriteAccess` about it, leaving an already-
              // active member's Favorites tab permanently stuck showing
              // "Join Membership" with no interactive stars for the
              // rest of the session. Recomputing here, on every
              // recognition update (not only the one at submit time),
              // closes that gap; `onWelcomeSubmit`'s own copy of this
              // logic is left in place and is now simply redundant in
              // the common case, never harmful to re-run.
              const isActiveMember = r?.member?.status === "active";
              setFavoriteAccess(isActiveMember ? "live" : "locked");
              if (isActiveMember && verifiedProofRef.current) {
                favorites.loadFavorites(verifiedProofRef.current);
              }
            }
            // Shared-kiosk correction (Aug 2026) — WelcomeModal calls
            // this with `null` the moment a typed phone/email no
            // longer matches a PRIOR recognition (its own identity-
            // invalidation effect) — i.e. a different customer has
            // started entering their own details on this same tab.
            // Any earlier customer's live Favorites/membership display
            // (and the stored proof behind it) must not survive that
            // moment, or the new customer could see the PRIOR
            // customer's Favorites tab before ever verifying anything
            // themselves.
            if (!r) endMemberSession();
          }}
          // Merge-blocker correction (Aug 2026) — persists the proof to
          // sessionStorage (slug-scoped, opaque, backend-revalidated on
          // every use) alongside lifting it into the ref, so the
          // restoration effect above can restore it after a browser
          // refresh. See `verifiedProofRef`'s own comment for the full
          // rationale.
          onVerifiedProof={(vp) => { verifiedProofRef.current = vp; setVerifiedProof(vp); setVerified(true); saveVerifiedProof(slug, vp); }}
          // Loyalty-only. Fires for BOTH real OTP flows — the welcome
          // step's own, and Past Orders' (the only one Dine-In/Takeout
          // offers). Deliberately does NOT touch `verifiedProofRef`,
          // `verified`, `favoriteAccess` or the persisted proof: this
          // authorizes showing a member their own points and nothing
          // else, and the backend re-proves the token on every call.
          onLoyaltyProof={(vp) => { if (vp?.token) setVerifiedProof(vp); }}
          recognize={recognizeCustomer}
          checkSafeStatus={checkSafeStatus}
          // Final Operator-Validation Prerequisites correction (Aug
          // 2026, Product Owner final OTP decision) — the no-OTP
          // counterpart to `onRecognized` above, fired only for
          // Dine-In/Takeout. `result` is `null` the instant the typed
          // phone/email is cleared/edited (mirrors the checkbox-lock
          // reset), or `{ active, phone, email }` once the SAFE lookup
          // settles. Deliberately does NOT touch `recognizedRef` or
          // `verifiedProofRef` — those remain exclusively for the real
          // OTP path (Past Orders) — only the display state (Favorites
          // access) and the safe, non-OTP identity used to authorize
          // Favorites calls.
          onSafeRecognized={(result) => {
            if (!result) {
              safeProofRef.current = null;
              // Walk-in loyalty verification (Sep 2026) — the typed
              // phone/email was cleared or edited, so the person in
              // front of us may not be the person we verified. A proof
              // obtained for LOYALTY only lives in state alone (never
              // in `verifiedProofRef`), so the guard below cannot
              // protect it and it must go now — otherwise the previous
              // customer's points and discounted total would still be
              // on screen for whoever is here next. A welcome-step OTP
              // proof does set the ref and is still protected by that
              // guard, exactly as before.
              if (!verifiedProofRef.current) setVerifiedProof(null);
              // Only clear the display state if there's no OTP-verified
              // session to fall back to — an OTP-verified member's
              // Favorites must never be interrupted by an unrelated
              // safe-check reset.
              if (!verifiedProofRef.current) {
                setFavoriteAccess("locked");
                setVerified(false);
                favorites.reset();
              }
              return;
            }
            safeProofRef.current = { phone: result.phone || null, email: result.email || null };
            setVerified(true);
            setFavoriteAccess(result.active ? "live" : "locked");
            if (result.active) {
              favorites.loadFavorites({ safe: true, phone: result.phone, email: result.email });
            }
          }}
          enrollMembershipSafe={async (body) => {
            const s =
              (tenant && (tenant.chatbot_slug || tenant.slug)) ||
              (catalog && (catalog.chatbot_slug || catalog.slug));
            if (!s) return null;
            const { data } = await api.post(`/public/retail/${s}/membership/enroll`, {
              catalog_id: catalog?.id,
              phone: body.phone,
              name: body.name || null,
              consent_text_version: body.consent_text_version,
              consent_source_url: body.consent_source_url || null,
            });
            return data;
          }}
          // Level 1 — Tenant Membership Foundation (Aug 2026). Same
          // slug-resolution + `api` client as `recognize` above — the
          // request body is exactly the just-completed OTP proof plus
          // explicit consent fields; the backend re-validates the proof
          // itself rather than trusting anything else in this payload.
          enrollMembership={async (body) => {
            const slug =
              (tenant && (tenant.chatbot_slug || tenant.slug)) ||
              (catalog && (catalog.chatbot_slug || catalog.slug));
            if (!slug) return null;
            const { data } = await api.post(`/public/retail/${slug}/membership/enroll`, {
              catalog_id: catalog?.id,
              checkout_attempt_token: body.checkout_attempt_token,
              destination_channel: body.destination_channel,
              destination_value: body.destination_value,
              // Live-acceptance correction (Aug 2026) — the backend now
              // independently re-derives SMS consent from the durable
              // consent-event evidence /otp/issue recorded for THIS
              // exact session, so the same sessionId already used for
              // every other call on this page must travel with the
              // enroll request too.
              session_id: sessionId,
              name: body.name || null,
              consent_text_version: body.consent_text_version,
              consent_source_url: body.consent_source_url || null,
            });
            return data;
          }}
          validateShippingAddress={async (body) => {
            // Phase 4 — the exact same validate-address endpoint
            // ShipToAddressPanel calls at checkout, called here instead
            // BEFORE product selection so Parcel Shipping never reaches
            // the menu with an unvalidated address.
            const s =
              (tenant && (tenant.chatbot_slug || tenant.slug)) ||
              (catalog && (catalog.chatbot_slug || catalog.slug));
            if (!s) return { ok: false, messages: ["Unable to validate address right now."] };
            const { data } = await api.post(
              `/retail/public/${s}/shipping/validate-address`,
              { ...body, catalog_id: catalog?.id || null },
            );
            return data;
          }}
          // Phase 3A.5 Final Checkout Integration correction — Local
          // Delivery mirrors the shipping validate/confirm pattern above,
          // but calls the separate Local-Delivery-only routes (never the
          // Parcel Shipping validate-address endpoint — the two stay
          // routing-isolated per engineering standards). Unlike
          // `validateShippingAddress`, these never let a caught error
          // propagate — a definitive provider rejection must be
          // distinguishable from a transient network failure, and the
          // address-validation gate is fail-CLOSED (Phase 3A.5 §14),
          // not fail-open like Parcel Shipping's carrier lookup.
          localDeliveryAddressValidationEnabled={!!tenant?.delivery_config?.address_validation_enabled}
          validateLocalDeliveryAddress={async (body) => {
            const s =
              (tenant && (tenant.chatbot_slug || tenant.slug)) ||
              (catalog && (catalog.chatbot_slug || catalog.slug));
            if (!s) return { ok: false, code: "unavailable", message: "Unable to validate address right now." };
            try {
              const { data } = await api.post(`/retail/public/${s}/local-delivery/address/validate`, body);
              return data;
            } catch (e) {
              const detail = e?.response?.data?.detail;
              return {
                ok: false,
                code: (detail && detail.code) || "address_validation_error",
                message: (detail && detail.message) || "We couldn't validate that address right now. Please try again.",
                fields: (detail && detail.fields) || null,
              };
            }
          }}
          confirmLocalDeliveryAddress={async (body) => {
            const s =
              (tenant && (tenant.chatbot_slug || tenant.slug)) ||
              (catalog && (catalog.chatbot_slug || catalog.slug));
            if (!s) return { ok: false, code: "unavailable", message: "Unable to confirm address right now." };
            try {
              const { data } = await api.post(`/retail/public/${s}/local-delivery/address/confirm`, body);
              return data;
            } catch (e) {
              const detail = e?.response?.data?.detail;
              return {
                ok: false,
                code: (detail && detail.code) || "address_confirmation_error",
                message: (detail && detail.message) || "We couldn't confirm that address right now. Please try again.",
              };
            }
          }}
        />
      )}

      {/* Gate C5A-3A — "Return to cart". `checkout()` flips the cart to
          `checked_out` before the storefront requests a delivery quote,
          so a failure after that point (unserviceable address, provider
          error) used to leave the customer staring at an empty shop with
          no indication their order still existed. The items were never
          deleted; nothing offered them back.

          This is an EXPLICIT action, never automatic: restoring on sight
          would quietly rebuild a cart the customer may have abandoned on
          purpose. One press produces one recovery — the server upserts
          on the same (company, session, open) tuple every other cart
          lookup uses, so a double-tap converges on one cart. */}
      {(phase === "menu" || phase === "confirmed") && recoverableCart
        && (recoverableCart.items?.length > 0) && (
        <div
          className="mx-auto max-w-3xl mt-3 mb-1 px-4"
          data-testid="retail-cart-recovery-banner"
        >
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-3">
            <span>
              Your order wasn't completed. We still have your{" "}
              {recoverableCart.items.length === 1
                ? "1 item"
                : `${recoverableCart.items.length} items`}.
            </span>
            <button
              type="button"
              onClick={recoverCart}
              disabled={recovering}
              data-testid="retail-cart-recovery-restore-btn"
              className={
                "rounded-md px-3 py-1.5 text-xs font-semibold text-white " +
                (recovering ? "bg-amber-400 cursor-not-allowed" : "bg-amber-600 hover:bg-amber-700")
              }
            >
              {recovering ? "Restoring\u2026" : "Return to cart"}
            </button>
          </div>
        </div>
      )}

      {(phase === "menu" || phase === "confirmed") && (
        <KioskShell
          tenant={tenant}
          catalog={catalog}
          customer={customer}
          orderType={effectiveOrderType}
          categories={categoriesForShell}
          itemsInCart={itemsInCart}
          cartLines={cartLines}
          cart={cart}
          currency={currency}
          cartCount={cartCount}
          labelFor={labelFor}
          onAdd={addProduct}
          onSetQty={setQty}
          onRemove={removeItem}
          onSetItemTakeout={setItemTakeout}
          spiceEnabled={spiceEnabled}
          onCheckout={() => setCheckoutOpen(true)}
          onMobileCart={() => setMobileCartOpen(true)}
          onNewOrder={requestNewOrder}
          onChat={chatEnabled ? goToChat : null}
          onInfo={(p) => setInfoProduct(p)}
          favoriteAccess={membershipEnabled ? favoriteAccess : undefined}
          verified={verified}
          favoritesData={{ loaded: favorites.loaded, items: favorites.items }}
          favoritedIds={favorites.favoritedIds}
          onToggleFavorite={(product) => favorites.toggleFavorite(currentFavoritesProof(), product.id)}
          onRemoveFavorite={(productId) => favorites.removeFavorite(currentFavoritesProof(), productId)}
          onRequireMembership={() => setPhase("welcome")}
          // Walk-in customer recognition/verification correction (Aug
          // 2026) — clicking a "verify" star or the unverified Favorites
          // panel routes back to the SAME welcome screen as
          // `onRequireMembership`; WelcomeModal's own state already
          // knows whether THIS customer still needs OTP verification or
          // only needs to check the membership box, so one shared
          // "return to welcome" action correctly serves both triggers.
          onRequireVerification={() => setPhase("welcome")}
          // Sticky Search (Sep 2026). Omitted entirely when the catalog
          // has Search switched off, so that storefront renders exactly
          // as it does today.
          search={searchEnabled ? search : undefined}
          catalogTruncated={!!catalogData?.catalog_truncated}
          totalProducts={catalogData?.total_products}
          onSelectProduct={handleSelectProduct}
          revealTarget={revealTarget}
          selectedProduct={selected.product}
          selectedProductLoading={selected.loading}
          selectedProductError={selected.error}
          onClearSelectedProduct={clearSelectedProduct}
          // Product Family navigation (Sep 2026). `familyTabs` is empty
          // for a catalog that has not switched the capability on, and
          // the shell renders no family row at all in that case.
          familyTabs={familyTabs}
          activeFamily={activeFamily}
          onFamilyChange={handleFamilyChange}
          filters={filters}
          navCategories={familyCategories}
        />
      )}

      {mobileCartOpen && (
        <MobileCartDrawer
          cart={cart}
          currency={currency}
          orderType={effectiveOrderType}
          onClose={() => setMobileCartOpen(false)}
          onSetQty={setQty}
          onRemove={removeItem}
          onSetItemTakeout={setItemTakeout}
          onCheckout={() => { setMobileCartOpen(false); setCheckoutOpen(true); }}
        />
      )}

      {checkoutOpen && cart && (
        <CheckoutModal
          slug={slug}
          linkSlug={linkSlug}
          sessionId={sessionId}
          hostDestination={hostDestination}
          tenant={tenant}
          catalog={catalog}
          cart={cart}
          currency={currency}
          customer={customer}
          orderType={effectiveOrderType}
          orderModes={orderModes}
          labelFor={labelFor}
          // Loyalty checkout visibility fix (Sep 2026) — hand checkout
          // the OTP proof this page already holds. Without it the
          // checkout modal starts with an empty token map (it only
          // fills one from its own submit-time VerificationModal), so a
          // member who verified at the welcome step reached checkout
          // with no proof and the loyalty panel had nothing to ask
          // with. Read through `verified` — real state, set alongside
          // every assignment to `verifiedProofRef.current` — so this
          // re-renders when the proof arrives; the ref alone would not.
          // The proof is the same opaque, server-issued token the rest
          // of this page passes around: it is re-validated backend-side
          // on every use and grants nothing on its own.
          verifiedProof={verifiedProof}
          // Walk-in loyalty verification (Sep 2026) — whether checkout
          // may offer "Verify to view and use loyalty points" at all.
          //
          // `verified` is exactly "this page recognizes who is standing
          // here", set by the safe non-OTP lookup as well as by a real
          // OTP. Gating the offer on it means an UNRECOGNIZED visitor is
          // shown nothing, so checkout never becomes a way to ask
          // whether a phone number has a membership or a balance. It
          // reveals no balance either way — the offer is an invitation,
          // and the points themselves stay behind the OTP.
          memberRecognized={verified}
          // The proof that comes back from that OTP. Same reactive state
          // the welcome-step and restore paths write, so it inherits the
          // whole lifetime already defined here: cleared by
          // `endMemberSession` (New Order, idle and absolute expiry), by
          // a tenant/catalog change, and by an identity change below.
          onLoyaltyVerified={(vp) => { if (vp?.token) setVerifiedProof(vp); }}
          onClose={() => setCheckoutOpen(false)}
          onPlaced={onPlaced}
          onShippingAddressChange={(addr) => {
            // Parcel Shipping Readiness & Checkout Completion — Phase 4.
            // Persist alongside the rest of the session-scoped customer
            // record so a closed-then-reopened checkout (or the customer
            // resuming a fresh session shortly after) doesn't force
            // re-entry of an address they already gave us.
            const updated = { ...(customer || {}), shippingAddress: addr };
            saveCustomer(slug, updated);
            setCustomer(updated);
          }}
        />
      )}

      {chatOpen && (
        <ChatEntryPanel
          slug={slug}
          sessionId={sessionId}
          tenant={tenant}
          onClose={() => setChatOpen(false)}
          onStartOrder={() => { setChatOpen(false); onStart(); }}
        />
      )}

      {infoProduct && (
        <ProductInfoModal
          product={infoProduct}
          currency={currency}
          onClose={() => setInfoProduct(null)}
        />
      )}

      {confirmNewOrder && (
        <ConfirmNewOrderModal
          onCancel={() => setConfirmNewOrder(false)}
          onConfirm={() => { setConfirmNewOrder(false); startNewOrder(); }}
        />
      )}

      {/*
        Go-Live Correction (Feb 2026) — Cash Payment Confirmation
        Workflow. If the order was placed via the dine-in / takeout
        "Pay at Counter" flow, the customer must NOT see the standard
        Order Confirmed screen (which offers Print Receipt + Start
        New Order) until staff records cash. Show the waiting screen
        instead and poll the public order endpoint; once the payment
        status flips to `paid` the kiosk auto-swaps to the confirmed
        modal without a browser refresh.
      */}
      {phase === "confirmed" && orderPlaced && (
        orderPlaced.awaiting_staff_payment && (orderPlaced.payment_status || "").toLowerCase() === "pending" ? (
          <AwaitingPaymentModal
            tenant={tenant}
            order={orderPlaced}
            slug={slug}
            pollFn={async (orderCode) => {
              // Note the path — backend route is registered at
              // `/api/retail/public/{slug}/orders/{code}` (retail_cart.py
              // prefix="/api/retail/public"), NOT `/api/public/retail`.
              // The earlier draft had these two path segments swapped,
              // which silently 404'd every poll and every cancel gesture.
              //
              // Security correction (Aug 2026) — this route now requires
              // the per-order access credential the checkout call just
              // stored (`saveOrderAccessToken` in CheckoutModal.jsx,
              // same tab/session this kiosk screen is already on).
              const { data } = await api.get(`/retail/public/${slug}/orders/${orderCode}`, {
                headers: { "X-Order-Access": loadOrderAccessToken(slug, orderCode) },
              });
              return data?.order || null;
            }}
            cancelFn={async (orderCode) => {
              const { data } = await api.post(
                `/retail/public/${slug}/orders/${orderCode}/cancel-awaiting-payment`,
                {},
                { headers: { "X-Order-Access": loadOrderAccessToken(slug, orderCode) } },
              );
              return data;
            }}
            onConfirmed={(fresh) => {
              // Merge the polled fields onto the placed-order snapshot
              // so the OrderConfirmedModal shows accurate payment state.
              setOrderPlaced((prev) => ({
                ...(prev || {}),
                ...(fresh || {}),
              }));
            }}
            onCancelled={(_fresh, _meta) => {
              // Reset the kiosk cleanly — cart already empty, just tear
              // down the confirmed phase so the customer returns to the
              // menu / landing. Any toast/notification is intentionally
              // omitted here to keep the file's dep surface unchanged
              // (this page doesn't currently import a toast library).
              startNewOrder();
            }}
          />
        ) : (
          <OrderConfirmedModal
            tenant={tenant}
            order={orderPlaced}
            slug={slug}
            onPrint={() => setPrintOpen(true)}
            onNewOrder={startNewOrder}
          />
        )
      )}

      {printOpen && orderPlaced && (
        <PrintReceipt
          tenant={tenant}
          catalog={catalog}
          order={orderPlaced}
          onClose={() => setPrintOpen(false)}
        />
      )}
    </div>
  );
}

// ----- gating chrome --------------------------------------------------------

function FullPageLoader() {
  return (
    <div className="min-h-screen bg-[#0d1f12] grid place-items-center" data-testid="retail-loading">
      <div className="text-center">
        <Loader2 className="w-7 h-7 animate-spin text-orange-400 mx-auto" />
        <div className="mt-3 text-sm text-stone-300">Loading menu…</div>
      </div>
    </div>
  );
}

function FullPageError({ gateError }) {
  return (
    <div className="min-h-screen bg-[#0d1f12] grid place-items-center px-6 text-center text-stone-100">
      <div className="max-w-sm">
        <h1 className="text-2xl font-semibold mb-2">Shop unavailable</h1>
        <p className="text-stone-300 mb-6" data-testid="retail-gate-error">{gateError}</p>
        <Link to="/" className="text-sm text-stone-300 underline">Return home</Link>
      </div>
    </div>
  );
}
